import { useMemo } from 'react';

import { fetchSitePages } from '@/lib/api';

import { useLocalizedQuery } from './useLocalizedQuery';

import type { AccessScope } from '@/lib/auth';
import type { UseQueryOptions } from '@tanstack/react-query';

import type { Page } from '@/lib/api/types';
import type { UUID } from '@/types';

export const useSitePages = (
  siteId: UUID | undefined,
  tenantId: string | undefined,
  scope: AccessScope,
  options?: Omit<UseQueryOptions<Page[], Error>, 'queryKey' | 'queryFn'>,
) => {
  const enabled = Boolean(siteId && tenantId) && (options?.enabled ?? true);

  const { data: pages, ...queryInfo } = useLocalizedQuery<Page[], Error>(
    ['sites-pages', siteId, tenantId, scope],
    (locale: string) => fetchSitePages(siteId!, tenantId!, { scope, locale }),
    { ...options, enabled },
  );

  const slugMap = useMemo<Record<string, Page>>(() => {
    if (!pages) return {};

    return pages.reduce(
      (acc, page) => {
        acc[page.slug] = page;
        return acc;
      },
      {} as Record<string, Page>,
    );
  }, [pages]);

  const getPageBySlug = (slug: string) => slugMap[slug];

  return { pages, getPageBySlug, ...queryInfo };
};
